/**
 * React Query hooks for Agent Handoffs
 *
 * Provides data fetching and mutations for handoff management
 * within a specific hive.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  Handoff,
  HandoffCreate,
  HandoffUpdate,
  HandoffListResponse,
} from '../types';

const HANDOFFS_KEY = 'handoffs';
const HIVES_KEY = 'hives';
const HIVE_GRAPH_KEY = 'hive-graph';

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetch all handoffs for a hive
 */
export function useHandoffs(hiveId: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [HANDOFFS_KEY, tenantId, hiveId],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<HandoffListResponse>(
        `/api/v1/hives/${hiveId}/handoffs`,
      );
      return data;
    },
    enabled: !!hiveId && !!tenantId,
  });
}

/**
 * Fetch single handoff by ID
 */
export function useHandoff(hiveId: string | undefined, handoffId: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [HANDOFFS_KEY, tenantId, hiveId, handoffId],
    queryFn: async () => {
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.get<Handoff>(
        `/api/v1/hives/${hiveId}/handoffs/${handoffId}`,
      );
      return data;
    },
    enabled: !!hiveId && !!handoffId && !!tenantId,
  });
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Create a new handoff in a hive
 */
export function useCreateHandoff(hiveId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (handoffData: HandoffCreate) => {
      const { data } = await apiClient.post<Handoff>(`/api/v1/hives/${hiveId}/handoffs`, handoffData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HANDOFFS_KEY] });
      queryClient.invalidateQueries({ queryKey: [HIVES_KEY] });
      queryClient.invalidateQueries({ queryKey: [HIVE_GRAPH_KEY] });
    },
  });
}

/**
 * Update a handoff
 */
export function useUpdateHandoff(hiveId: string, handoffId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (handoffData: HandoffUpdate) => {
      const { data } = await apiClient.patch<Handoff>(
        `/api/v1/hives/${hiveId}/handoffs/${handoffId}`,
        handoffData,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HANDOFFS_KEY] });
      queryClient.invalidateQueries({ queryKey: [HIVES_KEY] });
      queryClient.invalidateQueries({ queryKey: [HIVE_GRAPH_KEY] });
    },
  });
}

/**
 * Delete a handoff
 */
export function useDeleteHandoff(hiveId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (handoffId: string) => {
      await apiClient.delete(`/api/v1/hives/${hiveId}/handoffs/${handoffId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HANDOFFS_KEY] });
      queryClient.invalidateQueries({ queryKey: [HIVES_KEY] });
      queryClient.invalidateQueries({ queryKey: [HIVE_GRAPH_KEY] });
    },
  });
}
